// frontend/src/components/ReviewSubmitPage/EnhancedReviewSubmitPage.js
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Grid, Card, CardMedia, CardContent, CardActions, Typography, Button,
  Checkbox, FormControlLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, CircularProgress, Alert, Tabs, Tab, Paper, Tooltip, List,
  ListItem, ListItemAvatar, Avatar, ListItemText, IconButton, Divider, Menu,
  MenuItem, ListItemIcon, TextField, InputAdornment, TableSortLabel, Select,
  FormControl, InputLabel, Collapse, Autocomplete, Pagination, Stack
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SendIcon from '@mui/icons-material/Send';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import CancelScheduleSendIcon from '@mui/icons-material/CancelScheduleSend';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import RefreshIcon from '@mui/icons-material/Refresh';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';

import { 
  getUserImages, 
  submitBatchImagesToSyndigo,
  archiveBatchImages, 
  unarchiveBatchImages, 
  stageBatchImages, 
  unstageBatchImages,
  retrySubmitImage,
  deleteBatchImages,
  getImageFile,
  getCommodityValues,
  getUsers,
  getUserImagesWithPagination
} from '../../api/images'; 
import { scheduleSubmission, createBadgeRemovalSchedule, cancelScheduledSubmission, getScheduledSubmissionsForImage } from '../../api/scheduler';
import apiClient from '../../api/client';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import SubmitWithBadgeDialog from '../SubmitWithBadgeDialog';
import ScheduleConflictDialog from '../ScheduleConflictDialog';
import EditScheduleDialog from '../EditScheduleDialog';
import CommodityAutocomplete from '../Common/CommodityAutocomplete';
import ImageLazyLoader from '../ImageLazyLoader';
import SubCommodityAutocomplete from '../Common/SubCommodityAutocomplete';

const logger = console; 

const ApiStateHandler = ({ loading, error, data, children, noDataMessage = "No items found." }) => {
  if (loading) { // Controlled by main loading state only
    return ( <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4, minHeight: 200 }}> <CircularProgress /> <Typography sx={{ ml: 2 }}>Loading items...</Typography> </Box> );
  }
  if (error) { return <Alert severity="error" sx={{ m: 2 }}>{error.message || error}</Alert>; }
  if (!Array.isArray(data) || data.length === 0) {
    return ( <Box sx={{ textAlign: 'center', p: 3, minHeight: 200, display: 'flex', justifyContent: 'center', alignItems: 'center' }}> <Typography variant="body1">{noDataMessage}</Typography> </Box> );
  }
  return children;
};

const ItemActionMenu = ({ image, currentTabLabel, onEdit, onArchive, onRestore, onStage, onUnstage, onRetrySubmit, onDelete, isScheduled, onEditSchedule }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const navigate = useNavigate();
  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const handleViewDetails = () => { navigate(`/images/${image.id}`); handleClose(); };
  const handleEditAction = () => { onEdit(image.id); handleClose(); };
  const handleArchiveAction = () => { onArchive([image.id]); handleClose(); };
  const handleRestoreAction = () => { onRestore([image.id]); handleClose(); };
  const handleStageAction = () => { onStage([image.id]); handleClose(); };
  const handleUnstageAction = () => { onUnstage([image.id]); handleClose(); };
  const handleRetryAction = () => { onRetrySubmit(image.id); handleClose(); };
  const handleDeleteAction = () => { onDelete([image.id]); handleClose(); };
  const handleEditSchedule = () => { 
    if (onEditSchedule) {
      onEditSchedule(image.id);
    }
    handleClose(); 
  };

  return (
    <>
      <IconButton aria-label="actions" onClick={handleClick} size="small"><MoreVertIcon /></IconButton>
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        <MenuItem onClick={handleViewDetails}><ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>View Details</MenuItem>
        {currentTabLabel === "Scheduled" && isScheduled && (<MenuItem onClick={handleEditSchedule}><ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>Edit Schedule</MenuItem>)}
        {(currentTabLabel !== "Submitted" && currentTabLabel !== "Archived") && (<MenuItem onClick={handleEditAction}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Edit/Regenerate</MenuItem>)}
        {currentTabLabel === "Submitted" && (<MenuItem onClick={handleEditAction}><ListItemIcon><EditIcon fontSize="small" /></ListItemIcon>Edit (New Version)</MenuItem>)}
        {currentTabLabel === "Preview" && image.status === 'success' && !image.is_archived && image.is_staged && (<MenuItem onClick={handleUnstageAction}><ListItemIcon><CancelScheduleSendIcon fontSize="small" /></ListItemIcon>Remove Staging</MenuItem>)}
        {currentTabLabel === "Failed to Submit" && !image.is_archived && (<MenuItem onClick={handleRetryAction}><ListItemIcon><ReplayIcon fontSize="small" /></ListItemIcon>Retry Submission</MenuItem>)}
        {currentTabLabel === "Archived" && (<MenuItem onClick={handleRestoreAction}><ListItemIcon><RestoreFromTrashIcon fontSize="small" /></ListItemIcon>Restore to Preview</MenuItem>)}
        {["Preview", "Staged", "Failed Generation", "Failed to Submit"].includes(currentTabLabel) && !image.is_archived && (<MenuItem onClick={handleArchiveAction} sx={{color: 'warning.dark'}}><ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>Archive</MenuItem>)}
        {["Preview", "Failed Generation"].includes(currentTabLabel) && !image.is_archived && !image.is_submitted && (<MenuItem onClick={handleDeleteAction} sx={{color: 'error.main'}}><ListItemIcon><DeleteForeverIcon fontSize="small" /></ListItemIcon>Delete Permanently</MenuItem>)}
      </Menu>
    </>
  );
};

const EnhancedReviewSubmitPage = () => {
  const { user } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true); 
  const [isPollingActive, setIsPollingActive] = useState(false); // Tracks if a background poll is currently fetching
  const [error, setError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [editScheduleDialogOpen, setEditScheduleDialogOpen] = useState(false);
  const [scheduledSubmissionToEdit, setScheduledSubmissionToEdit] = useState(null);
  const [imagesToAction, setImagesToAction] = useState([]);
  const [conflictDialogOpen, setConflictDialogOpen] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState([]);
  const [isCancellingConflicts, setIsCancellingConflicts] = useState(false);
  const [processingAction, setProcessingAction] = useState('');
  const [actionStatus, setActionStatus] = useState(null); 
  const [currentTab, setCurrentTab] = useState(0); 
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'updated_at', direction: 'descending' });
  const [scheduledSubmissions, setScheduledSubmissions] = useState([]);
  const [showFilters, setShowFilters] = useState(true); // Show filters by default
  const [filters, setFilters] = useState({
    commodity: '',
    sub_commodity: '',
    created_after: '',
    created_before: '',
    gtin_search: '',
    user_filter: 'current' // 'current', 'all', or specific user ID
  });
  const [commodityOptions, setCommodityOptions] = useState({ commodities: [], sub_commodities: [] });
  const [usersList, setUsersList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20); // Default 20 for faster loading, user can change
  const [totalCount, setTotalCount] = useState(0);
  const navigate = useNavigate();
  const pollingIntervalRef = useRef(null); // For storing the interval ID

  const TABS_CONFIG = useMemo(() => [
    { index: 0, label: "Preview", apiParams: { status: 'success', submitted: false, archived: false }, view: 'grid', noData: "No images generated yet or ready for preview.", searchableFields: ['gtin', 'original_gtin', 'product_data.long_item_description', 'product_data.description'], poll: true },
    { index: 1, label: "Scheduled", apiParams: { archived: false }, view: 'list', noData: "No images are currently scheduled for submission.", searchableFields: ['gtin', 'original_gtin', 'product_data.long_item_description', 'product_data.description'], sortableKeys: ['gtin', 'product_data.long_item_description', 'updated_at'], poll: true },
    { index: 2, label: "Submitted", apiParams: { status: 'submitted', archived: false }, view: 'list', noData: "No images have been submitted yet.", searchableFields: ['gtin', 'original_gtin', 'product_data.long_item_description', 'product_data.description', 'status'], sortableKeys: ['gtin', 'product_data.long_item_description', 'updated_at'] },
    { index: 3, label: "Failed Generation", apiParams: { status: 'failed_generation', archived: false }, view: 'list', noData: "No images have failed generation.", searchableFields: ['gtin', 'original_gtin', 'reason'], sortableKeys: ['gtin', 'reason', 'updated_at'] },
    { index: 4, label: "Failed to Submit", apiParams: { status: 'failed_submission', archived: false }, view: 'list', noData: "No images have failed during submission.", searchableFields: ['gtin', 'original_gtin', 'reason'], sortableKeys: ['gtin', 'reason', 'updated_at'] },
    { index: 5, label: "Archived", apiParams: { archived: true }, view: 'list', noData: "No images are currently archived.", searchableFields: ['gtin', 'original_gtin', 'product_data.long_item_description', 'product_data.description'], sortableKeys: ['gtin', 'updated_at'] },
  ], []);
  const currentTabConfig = useMemo(() => TABS_CONFIG.find(tab => tab.index === currentTab) || TABS_CONFIG[0], [currentTab, TABS_CONFIG]);
  
  const listColumns = useMemo(() => [
    { id: 'thumbnail', label: '', sortable: false, width: '8%' },
    { id: 'gtin', label: 'GTIN', sortable: true, width: '15%' },
    { id: 'last_modified_by_email', label: 'Last Created By', sortable: true, width: '15%' },
    { id: 'product_data.long_item_description', label: 'Description', sortable: true, width: '28%' },
    { id: 'status', label: 'Status', sortable: false, width: '12%' },
    { id: 'updated_at', label: currentTab === 2 ? 'Scheduled For' : 'Last Updated', sortable: true, width: '14%' },
    { id: 'actions', label: 'Actions', sortable: false, align: 'center', width: '8%' }
  ], [currentTab]);

  const fetchImages = useCallback(async (isFullReload = true) => {
    if (isFullReload) { 
        setLoading(true); 
        setError(null);
        setSelectedImages([]); 
    } else {
        // If already polling, don't overlap.
        if (isPollingActive) {
            // logger.info("Polling skipped: Another poll is already active.");
            return;
        }
        setIsPollingActive(true);
        // logger.info(`Silent poll initiated for tab: ${currentTabConfig.label}`);
    }

    // Special handling for Scheduled tab - fetch scheduled submissions first
    if (currentTabConfig.label === "Scheduled") {
      try {
        // First fetch all scheduled submissions to get image IDs
        const scheduledResponse = await apiClient.get('/scheduler/scheduled?status=pending&all_users=true&limit=500');
        const scheduledData = scheduledResponse.data || [];
        setScheduledSubmissions(scheduledData);
        
        const scheduledImageIds = scheduledData.map(sub => sub.image_id);
        logger.info(`Scheduled tab: Found ${scheduledImageIds.length} scheduled images`);
        
        if (scheduledImageIds.length === 0) {
          setImages([]);
          setTotalCount(0);
          if (isFullReload) setLoading(false);
          setIsPollingActive(false);
          return;
        }
        
        // Now fetch those specific images with pagination
        const paginatedIds = scheduledImageIds.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        
        if (paginatedIds.length === 0) {
          setImages([]);
          setTotalCount(scheduledImageIds.length);
          if (isFullReload) setLoading(false);
          setIsPollingActive(false);
          return;
        }
        
        // Fetch the specific images
        const imagePromises = paginatedIds.map(id => 
          apiClient.get(`/images/${id}`).then(res => res.data).catch(() => null)
        );
        const fetchedImages = await Promise.all(imagePromises);
        const validImages = fetchedImages.filter(img => img !== null);
        
        setImages(validImages);
        setTotalCount(scheduledImageIds.length);
        if (isFullReload) setError(null);
      } catch (err) {
        logger.error('Error fetching scheduled images:', err);
        if (isFullReload) setError(err.response?.data || err.message || 'Failed to load scheduled images');
      } finally {
        if (isFullReload) setLoading(false);
        setIsPollingActive(false);
      }
      return;
    }

    const params = { 
      ...currentTabConfig.apiParams, 
      limit: pageSize, 
      skip: (currentPage - 1) * pageSize,
      // Add filter parameters if they have values
      ...(filters.commodity && { commodity: filters.commodity }),
      ...(filters.sub_commodity && { sub_commodity: filters.sub_commodity }),
      ...(filters.created_after && { created_after: filters.created_after }),
      ...(filters.created_before && { created_before: filters.created_before }),
      ...(filters.gtin_search && { gtin_search: filters.gtin_search }),
      // Add user filter - 'current' means current user (touched), 'all' means no filter, otherwise it's a specific user ID
      ...(filters.user_filter === 'current' && user && { touched_by_user_id: user.id }),
      ...(filters.user_filter !== 'current' && filters.user_filter !== 'all' && { user_id_filter: parseInt(filters.user_filter) })
    };
    
    // Log the exact parameters being sent to API
    logger.log(`Fetching images for tab '${currentTabConfig.label}' with params:`, params);
    
    try {
      const response = await getUserImagesWithPagination(params);
      
      // Extract items and total from response
      const data = response.items || [];
      const total = response.total || 0;
      
      setTotalCount(total);
      
      // Enhanced logging to debug tab display issue
      if (data && data.length > 0) {
        const imageStatuses = data.map(img => ({
          id: img.id,
          gtin: img.gtin,
          status: img.status,
          hasReason: !!img.reason,
          reason: img.reason?.substring(0, 50) + (img.reason?.length > 50 ? '...' : ''),
          staged: img.is_staged,
          submitted: img.is_submitted,
          archived: img.is_archived
        }));
        
        logger.log(`Tab '${currentTabConfig.label}' - Received ${data.length} images:`, imageStatuses);
        
        // Check for any anomalies
        if (currentTabConfig.label === 'Failed Generation') {
          const successImages = data.filter(img => img.status === 'success');
          if (successImages.length > 0) {
            logger.error('WARNING: Found success status images in Failed Generation tab!', successImages);
          }
        }
        
        if (currentTabConfig.label === 'Preview') {
          const failedImages = data.filter(img => img.status === 'failed_generation');
          if (failedImages.length > 0) {
            logger.error('WARNING: Found failed_generation status images in Preview tab!', failedImages);
          }
        }
      }
      
      setImages(data || []);
      if (isFullReload) setError(null);
      
      // Fetch scheduled submissions in parallel (don't wait for it)
      // This prevents blocking the UI while fetching scheduled submissions
      apiClient.get('/scheduler/scheduled?status=pending&all_users=true&limit=500')
        .then(scheduledResponse => {
          const scheduledData = scheduledResponse.data || [];
          logger.info(`Fetched ${scheduledData.length} scheduled submissions (all users)`, scheduledData);
          setScheduledSubmissions(scheduledData);
        })
        .catch(err => {
          logger.warn('Failed to fetch scheduled submissions:', err);
          setScheduledSubmissions([]);
        });
    } catch (err) {
      logger.error('Error fetching images:', err);
      if (isFullReload) setError(err.response?.data || err.message || 'Failed to load images');
    } finally {
      if (isFullReload) setLoading(false);
      setIsPollingActive(false); 
    }
  }, [currentTabConfig.apiParams, currentTabConfig.label, isPollingActive, pageSize, currentPage, filters, user]);

  // Effect for initial fetch and when tab changes
  useEffect(() => {
    // logger.info(`Tab changed to: ${currentTabConfig.label}. Triggering full image fetch.`);
    setCurrentPage(1); // Reset to first page when tab changes
    fetchImages(true); 
  }, [currentTab, filters]); // Depends on currentTab and filters.
  
  // Effect for pagination changes
  useEffect(() => {
    if (currentPage > 1 || pageSize !== 20) { // Only fetch if not initial state (updated default)
      fetchImages(true);
    }
  }, [currentPage, pageSize]);
  
  // Fetch commodity values and users when component mounts
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch commodity values
        const values = await getCommodityValues();
        setCommodityOptions(values);
        
        // Fetch users list for all users
        if (user) {
          const users = await getUsers();
          setUsersList(users);
        }
      } catch (error) {
        logger.error('Failed to fetch initial data:', error);
      }
    };
    fetchInitialData();
  }, [user]);

  // Effect for polling
  useEffect(() => {
    // Clear any existing interval when dependencies change
    if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null; // Reset ref
    }

    if (currentTabConfig.poll) {
        // logger.info(`Setting up polling for tab: ${currentTabConfig.label}`);
        pollingIntervalRef.current = setInterval(() => {
            // Only poll if no dialogs are open, not processing another action, 
            // not in a main loading state, and not already polling
            if (!submitDialogOpen && !archiveDialogOpen && !deleteDialogOpen && !processingAction && !loading && !isPollingActive) {
                // logger.info(`Polling interval fired for ${currentTabConfig.label}, conditions met, fetching...`);
                fetchImages(false); // false indicates a silent poll
            } else {
                // logger.info(`Polling interval fired for ${currentTabConfig.label}, conditions NOT met (dialogOpen: ${submitDialogOpen || archiveDialogOpen}, processing: ${processingAction}, loading: ${loading}, isPolling: ${isPollingActive}).`);
            }
        }, 15000); // Poll every 15 seconds
    }

    return () => { // Cleanup function
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            // logger.info(`Polling stopped for tab: ${currentTabConfig.label} on cleanup or tab change.`);
            pollingIntervalRef.current = null;
        }
    };
  // Dependencies that define *when to re-evaluate setting up the interval*:
  // currentTabConfig (which changes with currentTab), fetchImages (stable due to useCallback),
  // and states that should pause polling.
  }, [currentTabConfig, fetchImages, submitDialogOpen, archiveDialogOpen, deleteDialogOpen, processingAction, loading, isPollingActive]);


  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setSortConfig({ key: 'updated_at', direction: 'descending' });
    setSearchTerm(''); 
  };

  const handleSearchChange = (event) => setSearchTerm(event.target.value);
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
    setSortConfig({ key, direction });
  };
  
  const getNestedValue = (obj, path) => {
    if (!path) return obj;
    const properties = path.split('.');
    return properties.reduce((prev, curr) => (prev && typeof prev === 'object' && prev[curr] !== undefined && prev[curr] !== null) ? prev[curr] : null, obj);
  };

  const displayedImages = useMemo(() => {
    let processedImages = [...images];
    
    // No need for special filtering for Scheduled tab anymore - it's handled in fetchImages
    
    if (searchTerm) {
      const lowerSearchTerm = searchTerm.toLowerCase();
      processedImages = processedImages.filter(image => 
        currentTabConfig.searchableFields.some(field => {
          const value = getNestedValue(image, field);
          return value && String(value).toLowerCase().includes(lowerSearchTerm);
        })
      );
    }
    if (currentTabConfig.view === 'list' && sortConfig.key) {
      processedImages.sort((a, b) => { 
        const aValue = getNestedValue(a, sortConfig.key);
        const bValue = getNestedValue(b, sortConfig.key);
        if (aValue === null || aValue === undefined) return sortConfig.direction === 'ascending' ? 1 : -1;
        if (bValue === null || bValue === undefined) return sortConfig.direction === 'ascending' ? -1 : 1;
        if (sortConfig.key.includes('date') || sortConfig.key.includes('_at')) {
            return sortConfig.direction === 'ascending' ? new Date(aValue) - new Date(bValue) : new Date(bValue) - new Date(aValue);
        } else if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortConfig.direction === 'ascending' ? aValue - bValue : bValue - aValue;
        } else { 
            const strA = String(aValue).toLowerCase();
            const strB = String(bValue).toLowerCase();
            if (strA < strB) return sortConfig.direction === 'ascending' ? -1 : 1;
            if (strA > strB) return sortConfig.direction === 'ascending' ? 1 : -1;
            return 0;
        }
      });
    } else if (currentTabConfig.view === 'grid' && processedImages.length > 0) {
        processedImages.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
    }
    return processedImages;
  }, [images, searchTerm, sortConfig, currentTabConfig, scheduledSubmissions]);

  // useEffect(() => {
  //   logger.log("displayedImages updated, count:", displayedImages.length);
  // }, [displayedImages]);


  const handleSelectImage = (imageId) => {
    setSelectedImages((prev) => prev.includes(imageId) ? prev.filter((id) => id !== imageId) : [...prev, imageId]);
  };

  const handleSelectAll = (event) => {
    if (event.target.checked) setSelectedImages(displayedImages.map(img => img.id));
    else setSelectedImages([]);
  };
  
  const handleOpenDialog = async (actionType, imageIdsToAction) => {
    if (!imageIdsToAction || imageIdsToAction.length === 0) {
      logger.warn('No images provided to handleOpenDialog for action:', actionType);
      setActionStatus({ type: 'error', message: 'Please select at least one image.' });
      return;
    }
    const imageIds = Array.isArray(imageIdsToAction) ? imageIdsToAction : [imageIdsToAction];
    logger.info(`Opening ${actionType} dialog for images:`, imageIds);
    setImagesToAction(imageIds);
    setActionStatus(null);

    if (actionType === 'submit') {
      // Check for existing pending scheduled submissions before opening submit dialog
      try {
        const conflicts = [];
        for (const imageId of imageIds) {
          const pendingSubs = await getScheduledSubmissionsForImage(imageId, 'pending');
          if (pendingSubs.length > 0) {
            const image = images.find(img => img.id === imageId);
            if (image) {
              conflicts.push({ image, existingSubmissions: pendingSubs });
            }
          }
        }

        if (conflicts.length > 0) {
          setScheduleConflicts(conflicts);
          setConflictDialogOpen(true);
          return;
        }
      } catch (err) {
        logger.warn('Failed to check for schedule conflicts:', err);
      }

      setSubmitDialogOpen(true);
    }
    if (actionType === 'archive') setArchiveDialogOpen(true);
    if (actionType === 'delete') setDeleteDialogOpen(true);
  };

  const handleCancelConflictsAndProceed = async () => {
    setIsCancellingConflicts(true);
    try {
      for (const conflict of scheduleConflicts) {
        for (const sub of conflict.existingSubmissions) {
          await cancelScheduledSubmission(sub.id);
          logger.info(`Cancelled conflicting submission ${sub.id} for image ${conflict.image.gtin}`);
        }
      }
      setConflictDialogOpen(false);
      setScheduleConflicts([]);
      setSubmitDialogOpen(true);
    } catch (err) {
      logger.error('Failed to cancel conflicting submissions:', err);
      setActionStatus({ type: 'error', message: 'Failed to cancel existing scheduled submissions.' });
      setConflictDialogOpen(false);
      setScheduleConflicts([]);
    } finally {
      setIsCancellingConflicts(false);
    }
  };

  const handleCloseDialog = () => {
    if (processingAction) return;
    setSubmitDialogOpen(false);
    setArchiveDialogOpen(false);
    setDeleteDialogOpen(false);
  };
  
  const handleEditImage = (imageId) => {
    logger.info(`Navigating to edit image: ${imageId}`);
    navigate(`/images/${imageId}/edit`); 
  };

  const handleEditSchedule = (imageId) => {
    // Find the scheduled submission for this image
    const submission = scheduledSubmissions.find(sub => sub.image_id === imageId);
    if (submission) {
      setScheduledSubmissionToEdit(submission);
      setEditScheduleDialogOpen(true);
    } else {
      logger.error(`No scheduled submission found for image ${imageId}`);
    }
  };

  const handleBatchAction = async (actionType, apiFunc, imageIdsToActOn, successMsg, failureMsg, dialogCloseFunc = null) => {
    if (!imageIdsToActOn || imageIdsToActOn.length === 0) return;
    setProcessingAction(actionType); 
    setActionStatus(null);
    try {
      await apiFunc({ image_ids: imageIdsToActOn }); 
      setActionStatus({ type: 'success', message: successMsg(imageIdsToActOn.length) });
      setTimeout(() => {
        fetchImages(true); // Force a full, non-polling refresh after action
        if (dialogCloseFunc) dialogCloseFunc();
      }, 1500);
    } catch (err) {
      logger.error(`Error during ${actionType}:`, err);
      setActionStatus({ type: 'error', message: (err.message || err.detail) || failureMsg });
    } finally {
      setProcessingAction(''); 
      if (dialogCloseFunc && actionType !== 'submit') dialogCloseFunc();
      setImagesToAction([]); 
      setSelectedImages([]); 
    }
  };

  const handleStageSelected = () => handleBatchAction('stage', stageBatchImages, selectedImages, (c) => `${c} image(s) staged.`, 'Failed to stage images.');
  const handleUnstageSelected = () => handleBatchAction('unstage', unstageBatchImages, selectedImages, (c) => `${c} image(s) unstaged.`, 'Failed to unstage images.');
  const handleArchiveItemsDialog = () => handleBatchAction('archive', archiveBatchImages, imagesToAction, (c) => `${c} image(s) archived.`, 'Failed to archive images.', handleCloseDialog);
  const handleRestoreItems = (ids) => handleBatchAction('restore', unarchiveBatchImages, ids, (c) => `${c} image(s) restored.`, 'Failed to restore images.');
  const handleDeleteItemsDialog = () => handleBatchAction('delete', deleteBatchImages, imagesToAction, (c) => `${c} image(s) deleted.`, 'Failed to delete images.', handleCloseDialog);
  
  const handleRetrySingleSubmission = async (imageId) => {
    setProcessingAction(`retry-${imageId}`); setActionStatus(null);
    try {
      await retrySubmitImage(imageId);
      setActionStatus({ type: 'success', message: `Retrying submission for image ID ${imageId}. Refreshing...` });
      setTimeout(() => fetchImages(true), 2000);
    } catch (err) {
      logger.error(`Error retrying submission for image ${imageId}:`, err);
      setActionStatus({ type: 'error', message: (err.message || err.detail) || `Failed to retry submission for image ID ${imageId}.` });
    } finally {
      setProcessingAction('');
    }
  };
  
  const handleSubmitToSyndigo = async (scheduledDate = null, options = {}) => {
    const publishAfterSubmit = !!options.publishAfterSubmit;
    const publishOnSubmit = options.publishOnSubmit !== undefined ? !!options.publishOnSubmit : true;
    const productAttributeUpdates = options.productAttributeUpdates || null;
    // Validate that we have images to submit
    if (!imagesToAction || imagesToAction.length === 0) {
      logger.error('No images selected for submission');
      setActionStatus({ type: 'error', message: 'No images selected for submission.' });
      setSubmitDialogOpen(false);
      return;
    }
    
    setProcessingAction('submit'); setActionStatus(null);
    try {
      if (scheduledDate) {
        // Schedule the submission
        logger.info('Scheduling images for submission:', imagesToAction, 'for date:', scheduledDate);
        const scheduleDateTime = new Date(scheduledDate);
        logger.info('Parsed schedule date:', scheduleDateTime, 'ISO:', scheduleDateTime.toISOString());
        
        // Create schedule requests for each image
        const schedulePromises = imagesToAction.map(imageId => {
          logger.info(`Scheduling image ${imageId} for ${scheduleDateTime.toISOString()} publishOnSubmit=${publishOnSubmit}`);
          return scheduleSubmission(imageId, {
            scheduledFor: scheduleDateTime,
            createBadgeRemovalSuggestions: false,
            publishOnSubmit,
          });
        });
        
        const scheduleResults = await Promise.all(schedulePromises);

        // Immediately refresh scheduled submissions
        try {
          const scheduledResponse = await apiClient.get('/scheduler/scheduled?status=pending');
          setScheduledSubmissions(scheduledResponse.data || []);
        } catch (err) {
          logger.warn('Failed to fetch scheduled submissions after scheduling:', err);
        }

        setActionStatus({
          type: 'success',
          message: `${imagesToAction.length} image(s) scheduled for submission. You can view and manage scheduled submissions from the Scheduled tab.`
        });

        // Switch to Scheduled tab immediately
        const scheduledTabIndex = TABS_CONFIG.findIndex(t => t.label === "Scheduled");
        if (scheduledTabIndex !== -1) {
          setCurrentTab(scheduledTabIndex);
        }

        // Refresh images after a short delay to ensure tab switch is complete
        setTimeout(() => {
          fetchImages(true);
        }, 500);

        // Return parent submissions so badge removals can be scheduled by the dialog
        return scheduleResults.map(r => r.scheduled_submission);
      } else {
        // Submit immediately
        logger.info('Submitting images to Syndigo:', imagesToAction, 'publishAfterSubmit=', publishAfterSubmit);
        const submitted = await submitBatchImagesToSyndigo({
          image_ids: imagesToAction,
          publish_after_submit: publishAfterSubmit,
          product_attribute_updates: productAttributeUpdates,
        });
        const publishFailedImages = publishAfterSubmit
          ? (submitted || []).filter(img => typeof img?.reason === 'string' && img.reason.includes('Publish to recipient: failed'))
          : [];
        if (publishAfterSubmit && publishFailedImages.length > 0) {
          setActionStatus({
            type: 'warning',
            message: `${imagesToAction.length} image(s) submitted, but publish failed for ${publishFailedImages.length}. See the image's reason for details.`,
          });
        } else {
          setActionStatus({
            type: 'success',
            message: `${imagesToAction.length} image(s) ${publishAfterSubmit ? 'submitted and published' : 'submitted'} to Syndigo. Refreshing...`,
          });
        }
        setTimeout(() => {
          const submittedTabIndex = TABS_CONFIG.findIndex(t => t.label === "Submitted");
          setCurrentTab(submittedTabIndex !== -1 ? submittedTabIndex : 0);
          fetchImages(true);
        }, 2000);
      }
    } catch (err) {
      logger.error('Error submitting/scheduling images:', err);
      
      // Handle FastAPI validation errors
      let errorMessage = 'Failed to submit images to Syndigo.';
      if (err && err.detail && Array.isArray(err.detail)) {
        // FastAPI validation error format
        const validationErrors = err.detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        errorMessage = `Validation error: ${validationErrors}`;
      } else if (err && err.detail && typeof err.detail === 'string') {
        errorMessage = err.detail;
      } else if (err && err.message) {
        errorMessage = err.message;
      }
      
      setActionStatus({ type: 'error', message: errorMessage });
    } finally {
      setProcessingAction('');
      setSubmitDialogOpen(false);
      setImagesToAction([]);
      setSelectedImages([]);
    }
  };

  const handleCancelScheduledSubmissions = async () => {
    setProcessingAction('cancelSchedule');
    setActionStatus(null);
    
    try {
      const scheduledImageIds = scheduledSubmissions
        .filter(sub => sub.status === 'pending' && selectedImages.includes(sub.image_id))
        .map(sub => sub.id);
      
      logger.info('Canceling scheduled submissions:', scheduledImageIds);
      
      const cancelPromises = scheduledImageIds.map(id => 
        cancelScheduledSubmission(id)
      );
      
      await Promise.all(cancelPromises);
      
      setActionStatus({ 
        type: 'success', 
        message: `Canceled ${scheduledImageIds.length} scheduled submission(s).` 
      });
      
      // Refresh the images and scheduled submissions
      await fetchImages(true);
      setSelectedImages([]);
    } catch (err) {
      logger.error('Error canceling scheduled submissions:', err);
      setActionStatus({ 
        type: 'error', 
        message: 'Failed to cancel some scheduled submissions.' 
      });
    } finally {
      setProcessingAction('');
    }
  };

  const handleScheduleBadgeRemovals = async (badgeRemovals, parentSubmissionId = null) => {
    try {
      logger.info('Scheduling badge removals:', badgeRemovals, 'Parent ID:', parentSubmissionId);

      for (const removal of badgeRemovals) {
        try {
          const removalDate = removal.removalDate || removal.suggestedRemovalDate;

          if (parentSubmissionId) {
            // Create a child badge removal submission using the parent ID
            await createBadgeRemovalSchedule(
              parentSubmissionId,
              removal.badge,
              removalDate instanceof Date ? removalDate : new Date(removalDate)
            );
            logger.info(`Created child badge removal for parent ${parentSubmissionId}, badge ${removal.badge} on ${removalDate}`);
          } else {
            // For immediate submissions without a parent ID, create a standalone scheduled submission
            // with the is_badge_removal flag set to true
            await scheduleSubmission(removal.imageId, {
              scheduledFor: removalDate instanceof Date ? removalDate : new Date(removalDate),
              createBadgeRemovalSuggestions: false,
              isBadgeRemoval: true,  // Mark this as a badge removal submission
              badgesToRemove: [removal.badge]  // Specify which badge to remove
            });
            logger.info(`Scheduled standalone badge removal for image ${removal.imageId}, badge ${removal.badge} on ${removalDate}`);
          }
        } catch (err) {
          logger.error(`Failed to schedule badge removal for image ${removal.imageId}:`, err);
        }
      }

      setActionStatus({
        type: 'info',
        message: `${badgeRemovals.length} badge removal(s) scheduled.`
      });
    } catch (err) {
      logger.error('Error scheduling badge removals:', err);
    }
  };

  const handleScheduleSubmission = async (schedulingData) => {
    if (!imagesToAction || imagesToAction.length === 0) {
      logger.error('No images selected for scheduling');
      setActionStatus({ type: 'error', message: 'No images selected for scheduling.' });
      setScheduleDialogOpen(false);
      return;
    }
    
    setProcessingAction('schedule');
    setActionStatus(null);
    
    try {
      logger.info('Scheduling images for submission:', imagesToAction, schedulingData);
      
      // Get the selected images with full data
      const selectedImagesData = images.filter(img => imagesToAction.includes(img.id));
      
      // Schedule each image
      const promises = selectedImagesData.map(async (image) => {
        const response = await scheduleSubmission(image.id, {
          scheduledFor: new Date(schedulingData.scheduledFor),
          createBadgeRemovalSuggestions: schedulingData.badgeRemovals && schedulingData.badgeRemovals.length > 0
        });
        
        // Create badge removal schedules if requested
        if (schedulingData.badgeRemovals) {
          const badgeRemovalsForImage = schedulingData.badgeRemovals.filter(br => br.imageId === image.id);
          for (const badgeRemoval of badgeRemovalsForImage) {
            await createBadgeRemovalSchedule(
              response.scheduled_submission.id,
              badgeRemoval.badge,
              badgeRemoval.suggestedRemovalDate instanceof Date ? badgeRemoval.suggestedRemovalDate : new Date(badgeRemoval.suggestedRemovalDate)
            );
          }
        }
        
        return response.data;
      });
      
      await Promise.all(promises);
      
      setActionStatus({ 
        type: 'success', 
        message: `${imagesToAction.length} image(s) scheduled for submission. You can view and manage scheduled submissions from the Scheduled tab.` 
      });
      
      setTimeout(() => {
        fetchImages(true);
      }, 2000);
      
    } catch (err) {
      logger.error('Error scheduling images:', err);
      
      let errorMessage = 'Failed to schedule images.';
      if (err.response && err.response.data && err.response.data.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setActionStatus({ type: 'error', message: errorMessage });
    } finally {
      setProcessingAction('');
      setScheduleDialogOpen(false);
      setImagesToAction([]);
      setSelectedImages([]);
    }
  };

  const handleBulkDownload = async () => {
    if (selectedImages.length === 0) {
      logger.error('No images selected for download');
      setActionStatus({ type: 'error', message: 'No images selected for download.' });
      return;
    }
    
    setProcessingAction('download');
    setActionStatus(null);
    
    try {
      logger.info('Downloading images:', selectedImages);
      
      // Get the selected images with full data
      const selectedImagesData = images.filter(img => selectedImages.includes(img.id));
      
      // Download each image
      for (const image of selectedImagesData) {
        try {
          // Only download images that have been generated successfully
          if (image.status === 'success' || image.is_submitted || image.is_staged) {
            const blob = await getImageFile(image.id);
            if (blob && blob instanceof Blob && blob.size > 0) {
              // Create a temporary link to trigger download
              const link = document.createElement('a');
              const blobUrl = URL.createObjectURL(blob);
              link.href = blobUrl;
              link.download = `MRHI_${image.gtin}.png`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(blobUrl);
              
              // Add a small delay between downloads to avoid overwhelming the browser
              await new Promise(resolve => setTimeout(resolve, 100));
            }
          }
        } catch (err) {
          logger.error(`Failed to download image ${image.id}:`, err);
        }
      }
      
      setActionStatus({ 
        type: 'success', 
        message: `Downloaded ${selectedImages.length} image(s).` 
      });
      
    } catch (err) {
      logger.error('Error downloading images:', err);
      setActionStatus({ type: 'error', message: 'Failed to download some images.' });
    } finally {
      setProcessingAction('');
    }
  };
  
  const isImageScheduled = (imageId) => {
    return scheduledSubmissions.some(sub => sub.image_id === imageId && sub.status === 'pending');
  };

  const getScheduledSubmission = (imageId) => {
    return scheduledSubmissions.find(sub => sub.image_id === imageId && sub.status === 'pending');
  };

  const getStatusChip = (image) => {
    if (image.is_archived) return <Chip label="Archived" variant="outlined" size="small" icon={<DeleteForeverIcon />} />;
    if (image.status === 'failed_submission') return <Chip label="Submission Failed" color="error" variant="outlined" size="small" icon={<ReportProblemIcon />} />;
    if (image.is_submitted) return <Chip label="Submitted" color="primary" size="small" icon={<CheckCircleIcon />} />;
    if (image.status === 'success' && image.is_staged) {
      // Check if it's scheduled
      if (isImageScheduled(image.id)) {
        return <Chip label="Scheduled" color="info" size="small" icon={<ScheduleIcon />} />;
      }
      return <Chip label="Staged" color="secondary" size="small" icon={<PlaylistAddCheckIcon />} />;
    }
    if (image.status === 'success') return <Chip label="Preview Ready" color="success" size="small" icon={<CheckCircleOutlineIcon />} />;
    if (['processing', 'pending', 'pending_async'].includes(image.status)) return <Chip label="Processing" color="warning" size="small" icon={<HourglassEmptyIcon />} />;
    if (image.status === 'failed_generation' || image.status === 'failed') return <Chip label="Generation Failed" color="error" size="small" icon={<ErrorIcon />} />;
    return <Chip label={image.status || "Unknown"} size="small" />;
  };
  
  const isAllDisplayedSelected = displayedImages.length > 0 && selectedImages.length === displayedImages.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" sx={{ mb: 0 }}> Review & Manage MRHIs </Typography>
        <Button
            variant="outlined"
            size="small"
            onClick={() => fetchImages(true)} 
            startIcon={loading ? <CircularProgress size={18}/> : <RefreshIcon />}
            disabled={loading || isPollingActive} 
        >
            Refresh
        </Button>
      </Box>
      <Paper elevation={1} sx={{ mb: 1, borderRadius: 1, overflow: 'hidden' }}>
        <Tabs value={currentTab} onChange={handleTabChange} variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile indicatorColor="secondary" textColor="secondary">
          {TABS_CONFIG.map(tab => <Tab label={tab.label} key={tab.label} />)}
        </Tabs>
      </Paper>
      <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
        <TextField 
          fullWidth 
          size="small"
          variant="outlined" 
          placeholder={`Search in ${currentTabConfig.label}...`} 
          value={searchTerm} 
          onChange={handleSearchChange} 
          InputProps={{ 
            startAdornment: ( <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> ), 
          }} 
        />
        <Button
          variant="outlined"
          size="small"
          onClick={() => setShowFilters(!showFilters)}
          startIcon={<FilterListIcon fontSize="small" />}
          sx={{ minWidth: 100 }}
        >
          {showFilters ? 'Hide' : 'Filters'}
        </Button>
      </Box>
      
      <Collapse in={showFilters}>
        <Paper elevation={1} sx={{ p: 1, mb: 1 }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs={12} sm={6} md={2}>
              <CommodityAutocomplete
                value={filters.commodity}
                onChange={(value) => setFilters(prev => ({ ...prev, commodity: value }))}
                label="Commodity"
                placeholder="Select..."
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={2}>
              <SubCommodityAutocomplete
                value={filters.sub_commodity}
                onChange={(value) => setFilters(prev => ({ ...prev, sub_commodity: value }))}
                commodity={filters.commodity}
                multiple={false}
                label="Sub-commodity"
                placeholder="Type to search..."
                size="small"
                fullWidth
              />
            </Grid>
            
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="From"
                value={filters.created_after}
                onChange={(e) => setFilters(prev => ({ ...prev, created_after: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="To"
                value={filters.created_before}
                onChange={(e) => setFilters(prev => ({ ...prev, created_before: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            
            <Grid item xs={12} sm={4} md={2}>
              <TextField
                fullWidth
                size="small"
                label="GTIN"
                placeholder="Search..."
                value={filters.gtin_search}
                onChange={(e) => setFilters(prev => ({ ...prev, gtin_search: e.target.value }))}
              />
            </Grid>
            
            {/* User Filter - Available for all users */}
            <Grid item xs={12} sm={6} md={2}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>User</InputLabel>
                  <Select
                    value={filters.user_filter}
                    label="User"
                    onChange={(e) => setFilters(prev => ({ ...prev, user_filter: e.target.value }))}
                  >
                    <MenuItem value="current">My Images</MenuItem>
                    <MenuItem value="all">All Users</MenuItem>
                    {usersList.length > 0 && <Divider />}
                    {usersList.map(u => (
                      <MenuItem key={u.id} value={u.id.toString()}>
                        {u.username} ({u.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFilters({
                    commodity: '',
                    sub_commodity: '',
                    created_after: '',
                    created_before: '',
                    gtin_search: '',
                    user_filter: 'current' // Reset to current user
                  })}
                  sx={{ 
                    height: '40px',  // Match the height of the select
                    paddingLeft: '6px',
                    paddingRight: '6px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                    minWidth: '50px',
                    lineHeight: 1.2
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      </Collapse>
      
      {actionStatus && ( <Alert severity={actionStatus.type} sx={{ mb: 2 }} onClose={() => setActionStatus(null)}> {actionStatus.message} </Alert> )}
      {(!loading && displayedImages.length > 0) && (
        <Paper elevation={1} sx={{ mb: 1.5, p: 1, display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', borderRadius: 2, gap: 1 }}>
          <FormControlLabel control={ <Checkbox checked={isAllDisplayedSelected && selectedImages.length > 0} onChange={handleSelectAll} disabled={!!processingAction || displayedImages.length === 0} /> } label={`Select All Visible (${displayedImages.length})`} />
          <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap'}}>
            {currentTabConfig.label === "Preview" && ( 
              <>
                <Button 
                  variant="contained" 
                  color="primary" 
                  size="small" 
                  startIcon={processingAction === 'submit' ? <CircularProgress size={18} color="inherit"/> : <SendIcon />} 
                  onClick={() => handleOpenDialog('submit', selectedImages)} 
                  disabled={selectedImages.length === 0 || !!processingAction}
                > 
                  Submit Selected ({selectedImages.length})
                </Button>
              </> 
            )}
            {currentTabConfig.label === "Scheduled" && ( <> <Button variant="outlined" size="small" startIcon={<CancelScheduleSendIcon />} onClick={handleCancelScheduledSubmissions} disabled={selectedImages.length === 0 || !!processingAction}> {processingAction === 'cancelSchedule' ? <CircularProgress size={18} color="inherit"/> : `Cancel Schedule (${selectedImages.length})`} </Button> <Button variant="contained" color="primary" size="small" startIcon={processingAction === 'submit' ? <CircularProgress size={18} color="inherit"/> : <SendIcon />} onClick={() => handleOpenDialog('submit', selectedImages)} disabled={selectedImages.length === 0 || !!processingAction}> Submit Now ({selectedImages.length}) </Button> </> )}
            {/* Download button - available for all tabs with successfully generated images */}
            <Button 
              variant="outlined" 
              size="small" 
              startIcon={processingAction === 'download' ? <CircularProgress size={18} color="inherit"/> : <DownloadIcon />} 
              onClick={handleBulkDownload} 
              disabled={selectedImages.length === 0 || !!processingAction}
            > 
              {processingAction === 'download' ? 'Downloading...' : `Download (${selectedImages.length})`} 
            </Button>
            {["Preview", "Staged", "Failed Generation"].includes(currentTabConfig.label) && ( <Button variant="outlined" color="warning" size="small" startIcon={<DeleteForeverIcon />} onClick={() => handleOpenDialog('archive', selectedImages)} disabled={selectedImages.length === 0 || !!processingAction}> {processingAction === 'archive' ? <CircularProgress size={18} color="inherit"/> : `Archive Selected`} </Button> )}
            {["Preview", "Failed Generation"].includes(currentTabConfig.label) && ( <Button variant="outlined" color="error" size="small" startIcon={<DeleteForeverIcon />} onClick={() => handleOpenDialog('delete', selectedImages)} disabled={selectedImages.length === 0 || !!processingAction}> {processingAction === 'delete' ? <CircularProgress size={18} color="inherit"/> : `Delete Selected`} </Button> )}
          </Box>
        </Paper>
      )}
      <ApiStateHandler loading={loading} error={error} data={displayedImages} noDataMessage={currentTabConfig.noData}>
        {/* ... Grid and List rendering logic ... */}
        {currentTabConfig.view === 'grid' ? (
          <Grid container spacing={2}>
            {displayedImages.map((image) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={image.id}>
                <Card sx={{ display: 'flex', flexDirection: 'column', height: '100%', border: selectedImages.includes(image.id) ? '2px solid' : '1px solid', borderColor: selectedImages.includes(image.id) ? 'secondary.main' : 'divider', boxShadow: selectedImages.includes(image.id) ? 6 : 1, borderRadius: 2, '&:hover': { boxShadow: 4 } }}>
                  <Box sx={{ height: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', p: 1, backgroundColor: !(image.status === 'success' || image.is_submitted || image.is_staged) ? 'grey.200' : undefined }} onClick={() => navigate(`/images/${image.id}`)}>
                    <ImageLazyLoader 
                      src={(image.status === 'success' || image.is_submitted || image.is_staged) && image.file_path ? `${apiClient.defaults.baseURL}/images/${image.id}/thumbnail?size=400&t=${new Date(image.updated_at || image.created_at).getTime()}` : "https://placehold.co/600x400/eee/ccc?text=No+Preview"} 
                      alt={`MRHI for GTIN ${image.gtin}`}
                      className="mrhi-image"
                      placeholder="https://placehold.co/600x400/eee/ccc?text=Loading..."
                      onError={() => console.error("Image failed to load:", image.id)}
                    />
                  </Box>
                  <CardContent sx={{ flexGrow: 1, py:1 }}>
                    <Typography variant="subtitle2" noWrap title={image.gtin}>GTIN: {image.gtin}</Typography>
                    {(image.last_modified_by_email || image.user_email) && (
                      <Typography variant="caption" color="textSecondary" noWrap title={`Last created by: ${image.last_modified_by_email || image.user_email}`}>
                        By: {image.last_modified_by_email || image.user_email}
                      </Typography>
                    )}
                    {image.original_gtin && image.original_gtin !== image.gtin && ( <Tooltip title={`Original: ${image.original_gtin}`}><Typography variant="caption" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }}><InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Normalized</Typography></Tooltip> )}
                    {image.gtin_normalization_warning && ( <Tooltip title={image.gtin_normalization_warning}><Typography variant="caption" color="warning.dark" sx={{ display: 'flex', alignItems: 'center' }}><ReportProblemIcon fontSize="inherit" sx={{ mr: 0.5 }} /> Note</Typography></Tooltip> )}
                     <Box sx={{ mt: 0.5 }}>
                      {isImageScheduled(image.id) ? (
                        <Tooltip title={`Scheduled for ${new Date(getScheduledSubmission(image.id).scheduled_for).toLocaleString()}`}>
                          {getStatusChip(image)}
                        </Tooltip>
                      ) : (
                        getStatusChip(image)
                      )}
                    </Box>
                    {image.status === 'success' && image.reason && (
                      <Tooltip title={`Generation completed with warnings: ${image.reason}`}>
                        <Typography variant="caption" color="warning.main" sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <InfoIcon fontSize="inherit" sx={{ mr: 0.5 }} />
                          Has warnings
                        </Typography>
                      </Tooltip>
                    )}
                  </CardContent>
                  <Divider />
                  <CardActions sx={{ justifyContent: 'space-between', p:0.5 }}>
                    <Checkbox checked={selectedImages.includes(image.id)} onChange={() => handleSelectImage(image.id)} size="small" disabled={ (currentTabConfig.label === "Preview" && !(image.status === 'success' && !image.is_submitted && !image.is_archived)) || (currentTabConfig.label === "Staged" && !(image.status === 'success' && !image.is_submitted && !image.is_archived)) || (currentTabConfig.label === "Failed Generation" && image.status !== 'failed_generation') } />
                    <ItemActionMenu image={image} currentTabLabel={currentTabConfig.label} isScheduled={isImageScheduled(image.id)} onEdit={handleEditImage} onEditSchedule={handleEditSchedule} onArchive={handleOpenDialog.bind(null, 'archive')} onRestore={handleRestoreItems} onStage={handleStageSelected} onUnstage={handleUnstageSelected} onRetrySubmit={handleRetrySingleSubmission} onDelete={handleOpenDialog.bind(null, 'delete')} />
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : ( // LIST VIEW
          <Paper elevation={2} sx={{ borderRadius: 2 }}>
            <Box sx={{p: 1}}>
              <Box sx={{display: 'flex', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider', backgroundColor: 'grey.100', px: 1, py: 1, mb: 0}}>
                <Box sx={{width: 40}}></Box>
                {listColumns.map((col) => ( <Box key={col.id} sx={{width: col.width, fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: col.align === 'center' ? 'center' : 'flex-start', px:1}}> {col.sortable ? ( <TableSortLabel active={sortConfig.key === col.id} direction={sortConfig.key === col.id ? sortConfig.direction : 'asc'} onClick={() => requestSort(col.id)} IconComponent={sortConfig.key === col.id ? (sortConfig.direction === 'ascending' ? ArrowUpwardIcon : ArrowDownwardIcon) : undefined} > {col.label} </TableSortLabel> ) : ( col.label )} </Box> ))}
              </Box>
              {displayedImages.map((image, index) => (
                <React.Fragment key={image.id}>
                  <Box sx={{display: 'flex', alignItems: 'center', px: 1, py: 1, '&:hover': {backgroundColor: 'action.hover'}}} >
                    <Box sx={{width: 40, display: 'flex', justifyContent: 'center', alignItems: 'center'}}>
                      <Checkbox 
                        checked={selectedImages.includes(image.id)} 
                        onChange={() => handleSelectImage(image.id)} 
                        size="small"
                      />
                    </Box>
                    <Box sx={{width: listColumns.find(c=>c.id==='thumbnail')?.width, display: 'flex', justifyContent: 'center', alignItems: 'center', px:1}}>
                      {(image.status === 'success' || image.is_submitted || image.is_staged) && image.file_path ? (
                        <Box sx={{ width: 56, height: 56, cursor: 'pointer', borderRadius: 1, overflow: 'hidden', bgcolor: 'grey.100' }}>
                          <ImageLazyLoader
                            src={`${apiClient.defaults.baseURL}/images/${image.id}/thumbnail?size=100&t=${new Date(image.updated_at || image.created_at).getTime()}`}
                            alt={`MRHI for ${image.gtin}`}
                            placeholder="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='56' height='56'%3E%3Crect width='56' height='56' fill='%23e0e0e0'/%3E%3C/svg%3E"
                            onError={() => console.error("List thumbnail failed to load:", image.id)}
                            onClick={(e) => { 
                              e.stopPropagation(); 
                              navigate(`/images/${image.id}`);
                            }}
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                          />
                        </Box>
                      ) : (
                        <Avatar variant="rounded" sx={{ width: 56, height: 56, bgcolor: 'grey.300' }}>
                          <ReportProblemIcon />
                        </Avatar>
                      )}
                    </Box>
                    <Box sx={{width: listColumns.find(c=>c.id==='gtin')?.width, px:1}}><Typography variant="body2" noWrap sx={{fontWeight: 'medium'}}>{image.gtin}</Typography></Box>
                    <Box sx={{width: listColumns.find(c=>c.id==='last_modified_by_email')?.width, px:1}}><Typography variant="caption" color="textSecondary" noWrap title={image.last_modified_by_email || image.user_email}>{image.last_modified_by_email || image.user_email || 'Unknown'}</Typography></Box>
                    <Box sx={{width: listColumns.find(c=>c.id==='product_data.long_item_description')?.width, px:1, overflow: 'hidden'}}><Typography component="span" variant="caption" color="textSecondary" sx={{overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'}}>{image.product_description || getNestedValue(image, 'product_data.long_item_description') || getNestedValue(image, 'product_data.description') || 'No description'}</Typography></Box>
                     <Box sx={{width: listColumns.find(c=>c.id==='status')?.width, px:1}}>
                      {isImageScheduled(image.id) ? (
                        <Tooltip title={`Scheduled for ${new Date(getScheduledSubmission(image.id).scheduled_for).toLocaleString()}`}>
                          {getStatusChip(image)}
                        </Tooltip>
                      ) : (
                        getStatusChip(image)
                      )}
                    </Box>
                     <Box sx={{width: listColumns.find(c=>c.id==='updated_at')?.width, px:1}}>
                      {currentTabConfig.label === "Scheduled" && isImageScheduled(image.id) ? (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {new Date(getScheduledSubmission(image.id).scheduled_for).toLocaleString()}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="textSecondary" noWrap>
                          {new Date(image.updated_at || image.created_at).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                     <Box sx={{width: listColumns.find(c=>c.id==='actions')?.width, display: 'flex', justifyContent: 'center', alignItems: 'center', px:1}}> <ItemActionMenu image={image} currentTabLabel={currentTabConfig.label} onEdit={handleEditImage} onArchive={handleOpenDialog.bind(null, 'archive')} onRestore={handleRestoreItems} onStage={handleStageSelected} onUnstage={handleUnstageSelected} onRetrySubmit={handleRetrySingleSubmission} onDelete={handleOpenDialog.bind(null, 'delete')} /> </Box>
                  </Box>
                  {index < displayedImages.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </Box>
          </Paper>
        )}
      </ApiStateHandler>
      <ScheduleConflictDialog
        open={conflictDialogOpen}
        onClose={() => {
          setConflictDialogOpen(false);
          setScheduleConflicts([]);
        }}
        conflicts={scheduleConflicts}
        onCancelExistingAndProceed={handleCancelConflictsAndProceed}
        isCancelling={isCancellingConflicts}
      />
      <SubmitWithBadgeDialog
        open={submitDialogOpen}
        onClose={handleCloseDialog}
        selectedImages={images.filter(img => imagesToAction.includes(img.id))}
        onSubmit={handleSubmitToSyndigo}
        onScheduleBadgeRemovals={handleScheduleBadgeRemovals}
        isSubmitting={processingAction === 'submit'}
      />
      <Dialog open={archiveDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth> <DialogTitle>Confirm Archive</DialogTitle> <DialogContent> <Typography>Are you sure you want to archive {imagesToAction.length} selected image(s)?</Typography> <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>Archived images can be restored later.</Typography> </DialogContent> <DialogActions sx={{p:2}}> <Button onClick={handleCloseDialog} disabled={processingAction === 'archive'} color="inherit">Cancel</Button> <Button onClick={handleArchiveItemsDialog} color="warning" variant="contained" disabled={processingAction === 'archive'} startIcon={processingAction === 'archive' ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}> {processingAction === 'archive' ? 'Archiving...' : 'Confirm & Archive'} </Button> </DialogActions> </Dialog>
      <Dialog open={deleteDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth> <DialogTitle>Confirm Permanent Deletion</DialogTitle> <DialogContent> <Typography color="error">Are you sure you want to permanently delete {imagesToAction.length} selected image(s)?</Typography> <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>This action cannot be undone. Deleted images will not be visible to regular users.</Typography> <Typography variant="body2" color="error.main" sx={{ mt: 1, fontWeight: 'bold' }}>Only images that have not been submitted can be deleted.</Typography> </DialogContent> <DialogActions sx={{p:2}}> <Button onClick={handleCloseDialog} disabled={processingAction === 'delete'} color="inherit">Cancel</Button> <Button onClick={handleDeleteItemsDialog} color="error" variant="contained" disabled={processingAction === 'delete'} startIcon={processingAction === 'delete' ? <CircularProgress size={18} color="inherit" /> : <DeleteForeverIcon />}> {processingAction === 'delete' ? 'Deleting...' : 'Confirm & Delete'} </Button> </DialogActions> </Dialog>
      <EditScheduleDialog
        open={editScheduleDialogOpen}
        onClose={() => {
          setEditScheduleDialogOpen(false);
          setScheduledSubmissionToEdit(null);
        }}
        scheduledSubmission={scheduledSubmissionToEdit}
        onUpdate={() => {
          // Refresh the scheduled submissions and images
          fetchImages(true);
          setEditScheduleDialogOpen(false);
          setScheduledSubmissionToEdit(null);
        }}
      />
      
      {/* Pagination Controls */}
      {!loading && totalCount > 0 && (
        <Paper elevation={1} sx={{ mt: 2, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="body2">
                Showing {((currentPage - 1) * pageSize) + 1}-{Math.min(currentPage * pageSize, totalCount)} of {totalCount} items
              </Typography>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <InputLabel>Items per page</InputLabel>
                <Select
                  value={pageSize}
                  label="Items per page"
                  onChange={(e) => {
                    setPageSize(e.target.value);
                    setCurrentPage(1); // Reset to first page when changing page size
                  }}
                >
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Pagination
              count={Math.ceil(totalCount / pageSize)}
              page={currentPage}
              onChange={(event, value) => setCurrentPage(value)}
              color="primary"
              showFirstButton
              showLastButton
              siblingCount={1}
              boundaryCount={1}
            />
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default EnhancedReviewSubmitPage;
